import { type FC, useEffect, useMemo } from "react";
import { usePerformanceBreadcrumbs } from "../../../Common/Hooks/usePerformanceBreadcrumbs";
import ReportingFilter from "../../../../../components/reportingFilter/ReportingFilter";
import EventPerformanceBody from "./EventPerformanceBody";
import { FeatureFlags } from "../../../../../featureFlags";
import { useFeatureFlag } from "../../../../../hooks/useFeatureFlag";
import { type IWizardStep } from "interfaces";

import "../../../Common/utils/performanceSCSSUtils.scss";
import styles from "./eventPerformance.module.scss";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { selectIsExportActive } from "features/Reporting/state/export/exportSlice";
import ReportingExport from "components/reportingExport/ReportingExport";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";

export type Props = IWizardStep &
  PropsFromRedux & {
    eventId: number;
    eventTitle: string;
    isRecurring: boolean;
  };

export const EventPerformance: FC<Props> = ({
  eventId,
  eventTitle,
  isRecurring,
  acceptHandlers,
  isExportEnabled,
  accountId,
  accountType,
  publisherId,
  associatedFlows,
}) => {
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);

  const [filter, setFilter] = usePerformanceFilter(
    {
      includeAccountsDropdown: accountFilterShouldShow,
      accountId,
      includeDistinct: true,
      supportsSPType: true,
      includeFlows: associatedFlows !== undefined && associatedFlows.length > 0,
    },
    publisherId,
  );

  const reportEnabled = useFeatureFlag(FeatureFlags.EventReport);

  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs("Summary");

  useEffect(() => {
    acceptHandlers?.({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showFilter = useMemo(() => {
    return (
      <div className={styles.eventActions}>
        <ReportingFilter
          currentFilter={filter}
          callback={setFilter}
          includeAccountsDropdown={accountFilterShouldShow}
          includeAccounts={false}
          includeDistinct
          supportsSPType
          publisherId={publisherId}
          flowsData={associatedFlows}
        />
        {isExportEnabled && <ReportingExport />}
      </div>
    );
  }, [filter, accountFilterShouldShow, isExportEnabled, setFilter, publisherId, associatedFlows]);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={`${styles.performanceRoot} performanceRoot`}>
      <header className={styles.eventPerformanceHeader}>
        {!hasBreadcrumbsToDisplay ? <h2 className={styles.eventTitle}>Summary</h2> : <div>{domElements}</div>}
        {showFilter}
      </header>

      <div className={`${styles.performanceRoot} performanceRoot`}>
        <EventPerformanceBody
          eventId={eventId}
          eventTitle={eventTitle}
          registerBreadcrumb={registerBreadcrumb}
          filter={filter}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    isExportEnabled: selectIsExportActive(state),
    accountId: state.userProfile.accountId,
    accountType: state.userProfile.accountTypeId,
    publisherId: state.library.events.eventDetails.event.ownerId,
    associatedFlows: state.library.events.eventDetails.settings.flows,
  };
};

const mapDispatchToProps = (_dispatch: AppDispatch) => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EventPerformance);
export default ConnectedComponent;
